import { useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import { HERO_LOCATIONS, HERO_COLORS, latLngToVec3 } from "./constants";

/**
 * 3D pulsing markers + glassmorphic floating labels for every server region.
 * Markers are children of the earth group so they rotate with the planet.
 *
 * Interactive props:
 *   selectedId  — id of the currently focused location (or null)
 *   onSelect(loc) — invoked on click; parent handles the camera / rotation animation
 */
export default function LocationMarkers({ radius = 1, selectedId = null, onSelect }) {
  /* Render active marker last so its <Html/> label sits on top of any
     visually overlapping neighbours (e.g. LON/AMS/FRA cluster in Europe).
     Everything else follows the original array order. */
  const ordered = selectedId
    ? [...HERO_LOCATIONS.filter((l) => l.id !== selectedId),
       ...HERO_LOCATIONS.filter((l) => l.id === selectedId)]
    : HERO_LOCATIONS;
  return (
    <group>
      {ordered.map((loc) => (
        <Marker
          key={loc.id}
          loc={loc}
          radius={radius}
          active={selectedId === loc.id}
          onSelect={onSelect}
        />
      ))}
    </group>
  );
}

function Marker({ loc, radius, active, onSelect }) {
  const [x, y, z] = latLngToVec3(loc.lat, loc.lng, radius * 1.005);
  /* Orient the marker so the beam points outward from the sphere. */
  const outward = new THREE.Vector3(x, y, z).normalize();
  const quat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    outward,
  );
  const rootRef  = useRef();      // wraps everything so we can float / lift it
  const pulseRef = useRef();
  const dotRef   = useRef();
  const beamRef  = useRef();
  const activeRingRef = useRef(); // second larger expanding ring, only visible when active
  const [hovered, setHovered] = useState(false);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    /* -------- floating lift when active -------- */
    if (rootRef.current) {
      const targetLift = active ? 0.045 + Math.sin(t * 1.8) * 0.012 : 0;
      // Smooth exponential ease toward the target lift (frame-rate independent enough here)
      rootRef.current.position.x = x + outward.x * targetLift;
      rootRef.current.position.y = y + outward.y * targetLift;
      rootRef.current.position.z = z + outward.z * targetLift;
    }
    /* -------- base pulse (existing) -------- */
    if (pulseRef.current) {
      const pulse = 1 + Math.sin(t * (active ? 3.2 : 2.4) + loc.lat) * (active ? 0.55 : 0.35);
      pulseRef.current.scale.setScalar(pulse * (active ? 1.9 : 1.4));
      pulseRef.current.material.opacity = (active ? 0.85 : 0.55) - (pulse - 0.65) * 0.4;
    }
    /* -------- expanding ring (active only) -------- */
    if (activeRingRef.current) {
      if (active) {
        const t2 = (t * 0.9 + loc.lat) % 1;         // 0→1 cycle
        activeRingRef.current.visible = true;
        activeRingRef.current.scale.setScalar(1 + t2 * 4.2);
        activeRingRef.current.material.opacity = (1 - t2) * 0.75;
      } else {
        activeRingRef.current.visible = false;
      }
    }
    /* -------- dot glow -------- */
    if (dotRef.current) {
      const base = active ? 4.2 : (hovered ? 3.6 : 2.2);
      dotRef.current.material.emissiveIntensity = base + Math.sin(t * 3.0 + loc.lng) * 0.4;
      const scale = active ? 1.55 : (hovered ? 1.25 : 1.0);
      // Smoothly lerp scale
      dotRef.current.scale.setScalar(
        THREE.MathUtils.lerp(dotRef.current.scale.x, scale, 0.15),
      );
    }
    /* -------- beam -------- */
    if (beamRef.current) {
      const boost = active ? 0.25 : 0;
      beamRef.current.material.opacity = 0.35 + boost + Math.sin(t * 2.0 + loc.lat) * 0.15;
    }
  });

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(loc);
  };

  return (
    <group
      ref={rootRef}
      position={[x, y, z]}
      quaternion={quat}
    >
      {/* Vertical light beam shooting outward */}
      <mesh ref={beamRef} position={[0, 0.08, 0]}>
        <cylinderGeometry args={[0.004, 0.001, 0.16, 8, 1, true]} />
        <meshBasicMaterial
          color={active ? HERO_COLORS.pinkLight : HERO_COLORS.pink}
          transparent
          opacity={0.4}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Solid glowing dot — receives clicks & hovers */}
      <mesh
        ref={dotRef}
        onClick={handleClick}
        onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = "pointer"; }}
        onPointerOut={()  => { setHovered(false); document.body.style.cursor = "default"; }}
      >
        <sphereGeometry args={[0.012, 16, 16]} />
        <meshStandardMaterial
          color={active ? HERO_COLORS.glow : HERO_COLORS.pinkLight}
          emissive={active ? HERO_COLORS.pink : HERO_COLORS.pink}
          emissiveIntensity={2.4}
          toneMapped={false}
        />
      </mesh>

      {/* Neon pulse ring */}
      <mesh ref={pulseRef} rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.014, 0.022, 32]} />
        <meshBasicMaterial
          color={active ? HERO_COLORS.pink : HERO_COLORS.purple}
          transparent
          opacity={0.6}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Expanding ring — only visible when this marker is the active one */}
      <mesh ref={activeRingRef} rotation={[Math.PI / 2, 0, 0]} visible={false}>
        <ringGeometry args={[0.02, 0.028, 48]} />
        <meshBasicMaterial
          color={HERO_COLORS.pink}
          transparent
          opacity={0.0}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Glass floating label */}
      <Html
        position={[0, 0.055, 0]}
        center
        distanceFactor={2.4}
        occlude
        zIndexRange={[10, 0]}
        style={{ pointerEvents: "auto", userSelect: "none" }}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); if (onSelect) onSelect(loc); }}
          className="earth-label"
          data-hover={hovered ? "true" : "false"}
          data-active={active ? "true" : "false"}
          data-testid={`earth-label-${loc.id}`}
          aria-label={`Focus on ${loc.city}`}
        >
          <span className="earth-label__flag">{loc.flag}</span>
          <span className="earth-label__stack">
            <span className="earth-label__city">{loc.city}</span>
            <span className="earth-label__country">{loc.country}</span>
          </span>
        </button>
      </Html>
    </group>
  );
}
